/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { TaskListKey } from '../../../../ts/preferences';

// (Classic IA) Keys for Task List
export const HAS_SET_UP_ASSISTANT = 'hasSetUpAssistant';
export const HAS_LEARNED_CORE_DIALOG = 'hasLearnedCoreDialog';
export const HAS_IMPORTED_SEARCH_CONTENT = 'hasImportedSearchContent';

// (Classic IA) Local storage keys for Task List
export const HAS_VIEWED_PREVIEW = 'hasViewedPreview';
export const HAS_PLANNED_ASSISTANT = 'hasPlannedAssistant';
export const HAS_BUILT_ASSISTANT = 'hasBuiltAssistant';
export const HAS_SHARED_ASSISTANT = TaskListKey.HAS_SHARED_ASSISTANT; // (Shared by New IA)
export const HAS_IMPROVED_ASSISTANT_PERFORMANCE =
  'hasImprovedAssistantPerformance';
export const HAS_VIEWED_WEB_CHAT = 'hasViewedWebChat';
export const HAS_EMBEDDED_ASSISTANT = 'hasEmbeddedAssistant';

// (Classic IA) An array of each task's local storage keys, used to build out the tasks array within TaskList and
// remove the tasks from local storage when deleting an assistant.
// Note: The order of this array determines the overall order of tasks in the TaskList component
export const TASK_KEYS = [
  HAS_SET_UP_ASSISTANT,
  HAS_LEARNED_CORE_DIALOG,
  HAS_VIEWED_PREVIEW,
  HAS_PLANNED_ASSISTANT,
  HAS_BUILT_ASSISTANT,
  HAS_IMPORTED_SEARCH_CONTENT,
  HAS_SHARED_ASSISTANT,
  HAS_IMPROVED_ASSISTANT_PERFORMANCE,
  HAS_VIEWED_WEB_CHAT,
  HAS_EMBEDDED_ASSISTANT
];

// (New IA - Onboarding Task List) An array of each task's keys, used to build out the tasks arrays within TaskList.
// Note: The order of these arrays determine the overall order of tasks in the TaskList component
// "Get started" tasks array
export const GET_STARTED_TASKS = [
  {
    key: TaskListKey.HAS_LEARNED_WATSON_ASSISTANT,
    timeToComplete: 5
  },
  {
    key: TaskListKey.HAS_EXPLORED_LEARNING_CENTER,
    timeToComplete: 0 // Self-check task so no timeToComplete
  },
  {
    key: TaskListKey.HAS_EXPLORED_INTERACTIVE_DEMO,
    timeToComplete: 2
  }
];
export const CP4D_GET_STARTED_TASKS = [
  // No WalkMe on CP4D so only one 'Get started' task
  {
    key: TaskListKey.HAS_LEARNED_WATSON_ASSISTANT,
    timeToComplete: 5
  }
];
// "Create a conversation" tasks array
export const CREATE_CONVERSATION_TASKS = [
  {
    key: TaskListKey.HAS_CREATED_FIRST_ACTION,
    timeToComplete: 5
  },
  {
    key: TaskListKey.HAS_CREATED_STEPS,
    timeToComplete: 5
  },
  {
    key: TaskListKey.HAS_ADDED_FIVE_EXAMPLES,
    timeToComplete: 5
  }
];
// "Share with your team" tasks array
export const SHARE_WITH_TEAM_TASKS = [
  {
    key: TaskListKey.HAS_PREVIEWED_ASSISTANT,
    timeToComplete: 2.5
  },
  {
    key: TaskListKey.HAS_SHARED_ASSISTANT,
    timeToComplete: 2.5
  }
];

// (New IA - Task List) An array of each task's keys, used to build out the tasks arrays within TaskList.
// Note: The order of these arrays determine the overall order of tasks in the TaskList component
// "Enhance your assistant" tasks array
export const ENHANCE_ASSISTANT_TASKS = [
  {
    key: TaskListKey.HAS_VIEWED_GREETING_ACTION,
    timeToComplete: 2,
    doesExternalLaunch: false
  },
  {
    key: TaskListKey.HAS_BUILT_SECOND_ACTION,
    timeToComplete: 3,
    doesExternalLaunch: false
  },
  {
    key: TaskListKey.HAS_VIEWED_STEP_VALIDATION,
    timeToComplete: 2,
    doesExternalLaunch: false
  },
  {
    key: TaskListKey.HAS_VIEWED_CLARIFYING_QUESTION,
    timeToComplete: 2,
    doesExternalLaunch: false
  },
  {
    key: TaskListKey.HAS_VIEWED_RETRY_ACTION,
    timeToComplete: 2,
    doesExternalLaunch: false
  },
  {
    key: TaskListKey.HAS_VIEWED_FALLBACK_ACTION,
    timeToComplete: 2,
    doesExternalLaunch: false
  }
];
// "Test and refine your assistant" tasks array
export const TEST_ASSISTANT_TASKS = [
  {
    key: TaskListKey.HAS_VIEWED_DEBUG_MODE,
    timeToComplete: 3,
    doesExternalLaunch: false
  },
  {
    key: TaskListKey.HAS_VIEWED_ANALYTICS,
    timeToComplete: 5,
    doesExternalLaunch: false
  },
  {
    key: TaskListKey.HAS_ADDITIONAL_ACTIONS,
    timeToComplete: 0, // Self-check task so no timeToComplete
    doesExternalLaunch: false
  },
  {
    key: TaskListKey.HAS_REFINED_WITH_FEEDBACK,
    timeToComplete: 0, // Self-check task so no timeToComplete
    doesExternalLaunch: false
  }
];

// "Deploy your assistant" tasks array
export const COMMON_DEPLOY_TASKS = [
  {
    key: TaskListKey.HAS_PUBLISHED_ASSISTANT,
    timeToComplete: 2,
    doesExternalLaunch: false
  }
];
export const DEFAULT_DEPLOY_TASKS = [
  {
    key: TaskListKey.HAS_VIEWED_INTEGRATIONS,
    timeToComplete: 15,
    doesExternalLaunch: false
  }
];
export const WEB_CHAT_TASKS = [
  {
    key: TaskListKey.HAS_LEARNED_WEB_CHAT,
    timeToComplete: 2,
    doesExternalLaunch: true
  },
  {
    key: TaskListKey.HAS_SET_UP_WEB_CHAT,
    timeToComplete: 5,
    doesExternalLaunch: false
  },
  {
    key: TaskListKey.HAS_EMBEDDED_WEB_CHAT,
    timeToComplete: 5,
    doesExternalLaunch: false
  }
];
export const PHONE_TASKS = [
  {
    key: TaskListKey.HAS_LEARNED_PHONE,
    timeToComplete: 2,
    doesExternalLaunch: true
  },
  {
    key: TaskListKey.HAS_SET_UP_PHONE,
    timeToComplete: 5,
    doesExternalLaunch: false
  }
];
export const SMS_TASKS = [
  {
    key: TaskListKey.HAS_LEARNED_SMS,
    timeToComplete: 2,
    doesExternalLaunch: true
  },
  {
    key: TaskListKey.HAS_SET_UP_SMS,
    timeToComplete: 5,
    doesExternalLaunch: false
  }
];
export const SLACK_TASKS = [
  {
    key: TaskListKey.HAS_LEARNED_SLACK,
    timeToComplete: 2,
    doesExternalLaunch: true
  },
  {
    key: TaskListKey.HAS_SET_UP_SLACK,
    timeToComplete: 5,
    doesExternalLaunch: false
  }
];
export const FACEBOOK_TASKS = [
  {
    key: TaskListKey.HAS_LEARNED_FACEBOOK,
    timeToComplete: 2,
    doesExternalLaunch: true
  },
  {
    key: TaskListKey.HAS_SET_UP_FACEBOOK,
    timeToComplete: 5,
    doesExternalLaunch: false
  }
];
export const WHATS_APP_TASKS = [
  {
    key: TaskListKey.HAS_LEARNED_WHATS_APP,
    timeToComplete: 2,
    doesExternalLaunch: true
  },
  {
    key: TaskListKey.HAS_SET_UP_WHATS_APP,
    timeToComplete: 5,
    doesExternalLaunch: false
  }
];

// Both onboarding and dashboard task list task names
export const ALL_TASK_NAMES = {
  [TaskListKey.HAS_LEARNED_WATSON_ASSISTANT]: 'Learn about {productName}',
  [TaskListKey.HAS_EXPLORED_LEARNING_CENTER]: 'Explore your learning resources',
  [TaskListKey.HAS_EXPLORED_INTERACTIVE_DEMO]:
    'Explore {productName} features on our interactive demo site',
  [TaskListKey.HAS_CREATED_FIRST_ACTION]: 'Create your first action',
  [TaskListKey.HAS_CREATED_STEPS]: 'Create steps',
  [TaskListKey.HAS_ADDED_FIVE_EXAMPLES]: 'Add 5 examples',
  [TaskListKey.HAS_PREVIEWED_ASSISTANT]: 'Preview your assistant',
  [TaskListKey.HAS_SHARED_ASSISTANT]: 'Share your assistant',
  [TaskListKey.HAS_VIEWED_GREETING_ACTION]: 'Customize greeting',
  [TaskListKey.HAS_BUILT_SECOND_ACTION]: 'Build your second action',
  [TaskListKey.HAS_VIEWED_STEP_VALIDATION]: 'Establish step validation',
  [TaskListKey.HAS_VIEWED_CLARIFYING_QUESTION]:
    'Set up your assistant to ask clarifying questions',
  [TaskListKey.HAS_VIEWED_RETRY_ACTION]: 'Enable retry when no action matches',
  [TaskListKey.HAS_VIEWED_FALLBACK_ACTION]:
    'Design a fallback action and set up human agent escalation',
  [TaskListKey.HAS_VIEWED_DEBUG_MODE]:
    'Access the preview panel and test using debug mode',
  [TaskListKey.HAS_VIEWED_ANALYTICS]:
    'Get acquainted with your assistant‘s analytics',
  [TaskListKey.HAS_ADDITIONAL_ACTIONS]: 'Create additional actions',
  [TaskListKey.HAS_REFINED_WITH_FEEDBACK]:
    'Refine assistant based on team feedback',
  [TaskListKey.HAS_VIEWED_INTEGRATIONS]: 'Set up your assistant‘s channels',
  [TaskListKey.HAS_PUBLISHED_ASSISTANT]:
    'Publish your latest content to the live environment',
  [TaskListKey.HAS_LEARNED_WEB_CHAT]: 'Learn about the web chat integration',
  [TaskListKey.HAS_SET_UP_WEB_CHAT]: 'Set up your web chat integration',
  [TaskListKey.HAS_EMBEDDED_WEB_CHAT]: 'Embed your web chat on a test website',
  [TaskListKey.HAS_VIEWED_WEB_CHAT]: 'Customize web chat',
  [TaskListKey.HAS_LEARNED_PHONE]: 'Learn about the phone integration',
  [TaskListKey.HAS_SET_UP_PHONE]: 'Set up your phone integration',
  [TaskListKey.HAS_LEARNED_SMS]: 'Learn about integrating with SMS',
  [TaskListKey.HAS_SET_UP_SMS]: 'Set up your SMS integration',
  [TaskListKey.HAS_LEARNED_SLACK]: 'Learn about integrating with Slack',
  [TaskListKey.HAS_SET_UP_SLACK]: 'Set up your Slack integration',
  [TaskListKey.HAS_LEARNED_FACEBOOK]:
    'Learn about integrating with Facebook Messenger',
  [TaskListKey.HAS_SET_UP_FACEBOOK]:
    'Set up your Facebook Messenger integration',
  [TaskListKey.HAS_LEARNED_WHATS_APP]: 'Learn about integrating with WhatsApp',
  [TaskListKey.HAS_SET_UP_WHATS_APP]: 'Set up your WhatsApp integration'
};

// Other local storage keys
export const COPY_EMBED_LINK = 'userCopyEmbedLink';
export const SELECT_EMBED_TAB = 'selectEmbedTab';
export const HAS_FINISHED_WIZARD = 'hasFinishedWizard';

// (New IA) Remember if the user has already collapsed the informational
// banner shown on the Home page (above the task list).
export const HAS_COLLAPSED_HOMEPAGE_BANNER = 'hasCollapsedHomepageBanner';

// (New IA) Remember if the user has already closed the informational
// banner shown on the Actions page (above the data table).
export const ACTIONS_BANNER = 'ActionsList';
export const VARIABLES_BANNER = 'VariablesList';

export const ALL_ACTION_BANNER_NAMES = {
  [ACTIONS_BANNER]: 'hasClosedActionsBanner',
  [VARIABLES_BANNER]: 'hasClosedVariablesBanner'
};

// (New IA) Remember if the user has clicked on the "Don't remind me" button from the notification on the preview panel
export const HAS_SEEN_DIALOG_NOTIFICATION = 'hasSeenDialogNotification';
